/**
 * The Territory map (design tgDraw / tgZoomBy, L28988-29080), ported as it is: a county
 * choropleth of the open dollars the placed facilities carry, one pin per facility (sized
 * by open dollars, coloured by tier), pins nudged apart so none overlap, labels for the
 * selected facility and the two largest, scroll zoom and drag pan, and a fly-to when the
 * selection changes. d3, topojson-client and the us-atlas counties are bundled (the design
 * loads them from a CDN); the counties file is its own chunk, loaded when the map opens.
 *
 * Handoff RULE (Console backend handoff.md L716): a final fill or opacity is never set only
 * inside a transition. Pins get their final opacity directly; the one transition left is the
 * fade-out of a pin that is being removed.
 */
import { useEffect, useImperativeHandle, useRef, type Ref } from 'react';
import { geoBounds, geoContains, geoMercator, geoPath, type GeoPermissibleObjects, type GeoProjection } from 'd3-geo';
import { select, type Selection } from 'd3-selection';
import 'd3-transition';
import { zoom as d3zoom, zoomIdentity, zoomTransform, type ZoomBehavior } from 'd3-zoom';
import { feature } from 'topojson-client';
import type { Feature, Geometry } from 'geojson';
import { STATE_FIPS } from './model';

export interface MapPin {
  k: string;
  name: string;
  lat: number;
  lng: number;
  /** open dollars in thousands; 0 when not sized */
  amt: number;
  tier: string | null;
  skip: boolean;
  sel: boolean;
  col: string;
  label: string;
}

export interface MapHandle {
  zoomBy: (f: number) => void;
}

type County = Feature<Geometry, { name?: string }> & { id?: string | number; __bb?: [[number, number], [number, number]] };
type Topo = { objects: { counties: unknown } };

let topoPromise: Promise<Topo> | null = null;
/** The us-atlas counties, loaded once (a bundled chunk, never a CDN). */
const loadTopo = () => (topoPromise ??= import('us-atlas/counties-10m.json').then((m) => ((m as { default?: Topo }).default ?? m) as Topo));

interface Host extends HTMLDivElement {
  __loading?: boolean;
  __svg?: Selection<SVGSVGElement, unknown, null, undefined> | null;
  __g?: Selection<SVGGElement, unknown, null, undefined>;
  __all?: County[];
  __metro?: County[];
  __path?: ReturnType<typeof geoPath>;
  __proj?: GeoProjection;
  __w?: number;
  __h?: number;
  __sig?: string;
  __selK?: string | null;
  __zoom?: ZoomBehavior<SVGSVGElement, unknown>;
  __fitKey?: string;
}

const MSG = (t: string) => `<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:16px;color:#54606C;">${t}</div>`;

export function MapView({
  pins,
  counties,
  fitCounties,
  state,
  onPick,
  handle,
}: {
  pins: MapPin[];
  /** The metro's counties (labels); null = the counties that hold a pin. */
  counties: string[] | null;
  /** The counties the map fits to; null = the labelled ones. */
  fitCounties: string[] | null;
  /** The metro's state, for the county set when the server names none. */
  state: string | null;
  onPick: (k: string) => void;
  handle?: Ref<MapHandle>;
}) {
  const ref = useRef<Host>(null);
  const pickRef = useRef(onPick);
  pickRef.current = onPick;
  const topo = useRef<Topo | null>(null);
  const args = useRef({ pins, counties, fitCounties, state });
  args.current = { pins, counties, fitCounties, state };

  useImperativeHandle(handle, () => ({
    zoomBy: (f: number) => {
      const el = ref.current;
      if (!el?.__svg || !el.__zoom) return;
      const s = el.__svg;
      if (f === 0) s.transition().duration(450).call(el.__zoom.transform, zoomIdentity);
      else s.transition().duration(300).call(el.__zoom.scaleBy, f);
    },
  }));

  const draw = () => {
    const el = ref.current;
    if (!el) return;
    if (!topo.current) {
      if (!el.__loading) {
        el.__loading = true;
        el.innerHTML = MSG('Loading the metro map…');
        loadTopo()
          .then((j) => {
            topo.current = j;
            el.__loading = false;
            el.innerHTML = '';
            el.__svg = null;
            draw();
          })
          .catch(() => {
            el.innerHTML = MSG('Map could not load. The ranked list still works.');
          });
      }
      return;
    }
    const { pins: P0, counties: cty, fitCounties: fit, state: st } = args.current;
    const W = Math.max(320, el.clientWidth || 600),
      H = Math.max(240, el.clientHeight || 400);
    // The county set: the metro's (server) or the ones holding a pin; its state's counties are drawn.
    const fitKey = (cty ?? []).join(',') + '|' + (fit ?? []).join(',') + '|' + (st ?? '');
    if (!el.__svg || el.__w !== W || el.__h !== H || el.__fitKey !== fitKey) {
      const t = topo.current;
      const allUS = (feature(t as never, (t.objects as { counties: never }).counties) as unknown as { features: County[] }).features;
      const prefix = (fit?.[0] ?? cty?.[0] ?? '').slice(0, 2) || (st ? STATE_FIPS[st.toUpperCase()] : '') || '';
      const all = prefix ? allUS.filter((f) => String(f.id).indexOf(prefix) === 0) : allUS;
      all.forEach((f) => (f.__bb ??= geoBounds(f as GeoPermissibleObjects) as [[number, number], [number, number]]));
      let metro: County[];
      if (cty && cty.length) metro = all.filter((f) => cty.indexOf(String(f.id)) >= 0);
      else metro = all.filter((f) => P0.some((p) => inCounty(f, p)));
      const fitSet = fit && fit.length ? all.filter((f) => fit.indexOf(String(f.id)) >= 0) : metro.length ? metro : all;
      const proj = geoMercator().fitExtent(
        [
          [40, 70],
          [W - 40, H - 40],
        ],
        { type: 'FeatureCollection', features: fitSet } as GeoPermissibleObjects,
      );
      const path = geoPath(proj);
      el.innerHTML = '';
      const svg = select(el).append('svg').attr('width', W).attr('height', H).attr('viewBox', '0 0 ' + W + ' ' + H).style('display', 'block').style('cursor', 'grab');
      const g = svg.append('g');
      g.append('g').attr('class', 'tg-cty');
      g.append('g').attr('class', 'tg-lbl');
      g.append('g').attr('class', 'tg-pins');
      const zoom = d3zoom<SVGSVGElement, unknown>()
        .scaleExtent([1, 8])
        .on('zoom', (ev) => {
          g.attr('transform', ev.transform);
          g.selectAll<SVGGElement, unknown>('.tg-pin').attr('transform', function () {
            return this.getAttribute('data-t') + ' scale(' + 1 / ev.transform.k + ')';
          });
          g.selectAll('.tg-lbl text').attr('font-size', 10.5 / ev.transform.k);
        });
      svg.call(zoom);
      el.__zoom = zoom;
      el.__svg = svg;
      el.__g = g;
      el.__all = all;
      el.__metro = metro;
      el.__path = path;
      el.__proj = proj;
      el.__w = W;
      el.__h = H;
      el.__sig = '';
      el.__fitKey = fitKey;
    }
    const pins = P0;
    const sig = pins.map((p) => p.k + (p.sel ? '*' : '')).join('|');
    if (el.__sig === sig) return;
    const prevSel = el.__selK;
    el.__sig = sig;
    const g = el.__g!,
      path = el.__path!,
      proj = el.__proj!;
    const sums: Record<string, number> = {};
    pins.forEach((p) => {
      const f = el.__all!.filter((c) => inCounty(c, p))[0];
      if (f) sums[String(f.id)] = (sums[String(f.id)] || 0) + (p.amt || 0);
    });
    const ramp = (v: number | undefined) => (!v ? '#EFE9DC' : v < 300 ? '#E3EAF3' : v < 700 ? '#CFDCEC' : v < 1500 ? '#BACDE5' : v < 3000 ? '#A6BFDD' : '#93B1D6');
    g.select('.tg-cty')
      .selectAll<SVGPathElement, County>('path')
      .data(el.__all!, (f) => String(f.id))
      .join('path')
      .attr('d', (f) => path(f as GeoPermissibleObjects))
      .attr('stroke', '#D5CBB5')
      .attr('stroke-width', 0.6)
      .attr('vector-effect', 'non-scaling-stroke')
      .attr('fill', (f) => ramp(sums[String(f.id)]));
    g.select('.tg-lbl')
      .selectAll<SVGTextElement, County>('text')
      .data(el.__metro!, (f) => String(f.id))
      .join('text')
      .attr('x', (f) => path.centroid(f as GeoPermissibleObjects)[0])
      .attr('y', (f) => path.centroid(f as GeoPermissibleObjects)[1])
      .attr('text-anchor', 'middle')
      .attr('fill', 'rgba(34,39,46,.38)')
      .attr('font-family', 'Archivo, sans-serif')
      .attr('font-size', 10.5)
      .attr('font-weight', 700)
      .attr('letter-spacing', '.14em')
      .style('pointer-events', 'none')
      .attr('translate', 'no')
      .text((f) => (f.properties?.name || '').toUpperCase());
    const top = pins
      .filter((p) => p.amt)
      .slice()
      .sort((a, b) => b.amt - a.amt)
      .slice(0, 2)
      .map((p) => p.k);
    const k0 = zoomTransform(el.__svg!.node()!).k;
    const P: Record<string, { x: number; y: number; ox: number; oy: number; r: number }> = {};
    const rr = (p: MapPin) => (p.amt ? 4.5 + Math.sqrt(p.amt / 40) * 2.2 : 5);
    pins.forEach((p) => {
      const xy = proj([p.lng, p.lat])!;
      P[p.k] = { x: xy[0], y: xy[1], ox: xy[0], oy: xy[1], r: rr(p) };
    });
    const ks = Object.keys(P);
    for (let it = 0; it < 120; it++) {
      for (let i = 0; i < ks.length; i++)
        for (let j = i + 1; j < ks.length; j++) {
          const a = P[ks[i]],
            b = P[ks[j]];
          let dx = b.x - a.x,
            dy = b.y - a.y;
          let d = Math.sqrt(dx * dx + dy * dy) || 0.01;
          const min = a.r + b.r + 4;
          if (d < min) {
            const m = (min - d) / 2 / d;
            if (d < 0.02) {
              dx = i % 2 ? 1 : -1;
              dy = j % 2 ? 1 : -1;
              d = 1.4;
            }
            a.x -= dx * m;
            a.y -= dy * m;
            b.x += dx * m;
            b.y += dy * m;
          }
        }
      ks.forEach((k) => {
        const q = P[k];
        q.x += (q.ox - q.x) * 0.02;
        q.y += (q.oy - q.y) * 0.02;
      });
    }
    const sel = g.select('.tg-pins').selectAll<SVGGElement, MapPin>('g.tg-pin').data(pins, (p) => p.k);
    sel.exit().transition().duration(250).style('opacity', 0).remove();
    const ent = sel.enter().append('g').attr('class', 'tg-pin').style('cursor', 'pointer').style('opacity', 0);
    const all = ent.merge(sel);
    // A label's width as drawn (14px, weight 800), measured where the browser can.
    const probe = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    probe.setAttribute('font-family', 'Archivo, sans-serif');
    probe.setAttribute('font-size', '14');
    probe.setAttribute('font-weight', '800');
    probe.setAttribute('visibility', 'hidden');
    el.__svg!.node()!.appendChild(probe);
    const LB = placeLabels(
      pins.map((p) => ({ k: p.k, name: p.name, label: p.label, sel: !!p.sel, top: top.indexOf(p.k) >= 0, x: P[p.k].x, y: P[p.k].y, r: rr(p) })),
      k0,
      (txt) => {
        probe.textContent = txt;
        try {
          return probe.getComputedTextLength();
        } catch {
          return 0;
        }
      },
    );
    probe.remove();
    all.each(function (p) {
      const q = P[p.k];
      const t = 'translate(' + q.x.toFixed(1) + ',' + q.y.toFixed(1) + ')';
      this.setAttribute('data-t', t);
      this.setAttribute('data-k', p.k);
      this.setAttribute('transform', t + ' scale(' + 1 / k0 + ')');
      const r = rr(p);
      const lbl = !!LB[p.k];
      this.innerHTML =
        '<title>' + esc(p.name + ' · ' + p.label) + '</title>' +
        (p.sel || p.tier === 'A'
          ? '<circle r="' + r + '" fill="none" stroke="' + p.col + '" stroke-width="1.6"><animate attributeName="r" values="' + r + ';' + (r + 14) + ';' + r + '" dur="2s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.7;0;0.7" dur="2s" repeatCount="indefinite"/></circle>'
          : '') +
        '<circle r="' + r + '" fill="' + (p.skip ? '#FCFAF4' : p.col) + '" stroke="' + (p.skip ? p.col : '#FCFAF4') + '" stroke-width="' + (p.sel ? 2.6 : 1.5) + '"/>' +
        (p.sel ? '<circle r="' + (r + 4) + '" fill="none" stroke="#22272E" stroke-width="1.4"/>' : '') +
        (lbl
          ? '<text x="' + (LB[p.k].x || 0) + '" y="' + LB[p.k].y + '" text-anchor="' + (LB[p.k].a || 'middle') + '" font-family="Archivo, sans-serif" font-size="14" font-weight="800" fill="#22272E" stroke="#F7F3EA" stroke-width="3" paint-order="stroke">' + esc(LB[p.k].txt) + '</text>'
          : '');
    });
    all
      .filter((p) => !!LB[p.k])
      .sort((a, b) => (a.sel ? 1 : 0) - (b.sel ? 1 : 0))
      .raise();
    ent.style('opacity', 1);
    const s = pins.filter((p) => p.sel)[0];
    if (s && prevSel !== undefined && prevSel !== s.k && el.__zoom) {
      const xy = [P[s.k].x, P[s.k].y];
      const k = Math.max(1.8, k0);
      el.__svg!.transition().duration(700).call(el.__zoom.transform, zoomIdentity.translate(W / 2 - k * xy[0], H / 2 - k * xy[1]).scale(k));
    }
    el.__selK = s ? s.k : null;
  };

  // Draw on every change of the pins or the county set (the design redraws on every render
  // and skips when nothing moved; the signature check below does the same).
  useEffect(() => {
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pins, counties, fitCounties, state]);

  // Clicks on a pin select it; a resize redraws at the new size.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const click = (ev: MouseEvent) => {
      const t = (ev.target as Element | null)?.closest?.('[data-k]');
      if (t) pickRef.current(t.getAttribute('data-k') as string);
    };
    el.addEventListener('click', click);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        const W = Math.max(320, el.clientWidth || 600),
          H = Math.max(240, el.clientHeight || 400);
        if (el.__svg && (W !== el.__w || H !== el.__h)) draw();
      });
      ro.observe(el);
    }
    return () => {
      el.removeEventListener('click', click);
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={ref} data-testid="tg-map" style={{ position: 'absolute', inset: 0 }} />;
}

/** Is the pin inside the county (bounding box first, so a metro of hundreds stays quick)? */
type Box = { x0: number; x1: number; y0: number; y1: number };
export interface LabelPin { k: string; name: string; label: string; sel: boolean; top: boolean; x: number; y: number; r: number }
export type PlacedLabel = { x: number; y: number; a: string; txt: string };

/**
 * Which pins carry a label, and where: the selected pin first, then the top pins, each only
 * where its label touches no other label and no other pin (above, below, right, left, then
 * further out). The selected pin is always named. A label carries the whole name, never
 * cut mid-name. Positions are in map units at zoom k; measure gives a label's drawn width
 * (0 when it cannot, then a generous estimate is used).
 */
export function placeLabels(pins: LabelPin[], k: number, measure: (txt: string) => number = () => 0): Record<string, PlacedLabel> {
  const LB: Record<string, PlacedLabel> = {};
  const placed: Box[] = [];
  const over = (a: Box, b: Box) => a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;
  const pinBox = (p: LabelPin): Box => ({ x0: p.x - p.r / k, x1: p.x + p.r / k, y0: p.y - p.r / k, y1: p.y + p.r / k });
  pins
    .filter((p) => p.sel || p.top)
    .sort((a, b) => (b.sel ? 1 : 0) - (a.sel ? 1 : 0))
    .forEach((p) => {
      const r = p.r;
      const txt = p.name + ' · ' + p.label;
      const w = Math.max(measure(txt) || 0, txt.length * 7) + 8;
      const hit = (box: Box) => placed.some((b) => over(box, b)) || pins.some((o) => o.k !== p.k && over(box, pinBox(o)));
      const tries: Array<[number, number, string]> = [
        [0, -r - 6, 'middle'],
        [0, r + 15, 'middle'],
        [r + 5, 4, 'start'],
        [-r - 5, 4, 'end'],
        [0, -r - 20, 'middle'],
        [0, r + 29, 'middle'],
      ];
      for (const tr of tries) {
        const x0 = tr[2] === 'start' ? p.x + tr[0] / k : tr[2] === 'end' ? p.x + (tr[0] - w) / k : p.x - w / 2 / k;
        const box = { x0, x1: x0 + w / k, y0: p.y + (tr[1] - 11) / k, y1: p.y + (tr[1] + 3) / k };
        if (!hit(box)) {
          placed.push(box);
          LB[p.k] = { x: tr[0], y: tr[1], a: tr[2], txt };
          return;
        }
      }
      // The selected pin is always named: with nowhere free, it takes the spot above.
      if (p.sel) {
        const x0 = p.x - w / 2 / k;
        placed.push({ x0, x1: x0 + w / k, y0: p.y + (-r - 17) / k, y1: p.y + (-r - 3) / k });
        LB[p.k] = { x: 0, y: -r - 6, a: 'middle', txt };
      }
    });
  return LB;
}

function inCounty(f: County, p: { lat: number; lng: number }): boolean {
  const bb = f.__bb;
  if (bb && bb[0][0] <= bb[1][0] && (p.lng < bb[0][0] || p.lng > bb[1][0] || p.lat < bb[0][1] || p.lat > bb[1][1])) return false;
  return geoContains(f as GeoPermissibleObjects, [p.lng, p.lat]);
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
